import {
  Box,
  Button,
  Paper,
  Step,
  StepLabel,
  Stepper,
  StepConnector,
  stepConnectorClasses,
  useTheme,
  Grid2,
  Typography,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useState, ReactNode, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  ConfigureRelatorio,
  ConfigureHosts,
  IntervaloColeta,
} from "../../../../componentes/easyReport/stepsNovoRelatorio";
import { styled } from "@mui/material/styles";
import { ArrowBack } from "@mui/icons-material";
import * as dayjs from "dayjs";
import * as utc from "dayjs/plugin/utc";
import * as timezone from "dayjs/plugin/timezone";
import { PdfPreview } from "../../../../componentes/easyReport/pdfPreview/pdfPreview";
import * as JSON5 from "json5";
import api from "../../../../services/api";
import { notifyOnPage } from "../../../../utils/notifyOnPage";
import { socketCliente } from "../../../../services/socket";

dayjs.extend(utc);
dayjs.extend(timezone);

interface StepType {
  label: string;
  description: ReactNode;
}
interface OutletContextType {
  config: EasyReportConfig[];
  setConfig: React.Dispatch<React.SetStateAction<EasyReportConfig[]>>;
}
interface HostErrorType {
  perfilIdx: number;
  variavelIdx: number;
  hostIdx: number;
  message: string;
  field?: "host" | "item" | "custom"; // added to target specific inputs
}

export default function NovoRelatorio() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [relatorioData, setRelatorioData] = useState<RelatorioDataType>({
    title: "",
    perfil: [],
    hideGraphs: false,
  });
  const [hostData, setHostData] = useState<HostDataType[]>([]);
  const [perfil, setPerfil] = useState<PerfilDataType[]>([]);
  const [relatorioLoad, setRelatorioLoad] = useState(false);
  const [relatorioEditData, setRelatorioEditData] = useState<any>(null);
  const [previousPerfilIds, setPreviousPerfilIds] = useState<string[]>([]);
  const [isEditDataProcessed, setIsEditDataProcessed] = useState(false);
  const [isConfigInitialized, setIsConfigInitialized] = useState(false);
  const [hostErrors, setHostErrors] = useState<HostErrorType[]>([]);
  const [showHostErrors, setShowHostErrors] = useState(false);
  const [relatorioErrors, setRelatorioErrors] = useState<{
    title?: string;
    perfil?: string;
  }>({});
  const [showRelatorioErrors, setShowRelatorioErrors] = useState(false);
  const [intervalData, setIntervalData] = useState<{
    dataInicial: any;
    dataFinal: any;
    observacao?: string;
    intervalosRemovidos?: any[];
  }>(() => {
    const now = dayjs();
    return {
      dataInicial: now.subtract(1, "M").startOf("day"),
      dataFinal: now.startOf("day"),
      observacao: "",
      intervalosRemovidos: [],
    };
  });
  const [variaveisPreview, setVariaveisPreview] = useState<Variaveis[][]>([]);
  const { id } = useParams();

  const { config } = useOutletContext<OutletContextType>();

  const monthLimit = useMemo(() => {
    return config.find((c) => c.nome === "geral")?.configuracoes?.interval;
  }, [config]);

  const timeZone = useMemo(() => {
    const result = config?.find((c) => c.nome === "geral");
    if (result) {
      return result.configuracoes.timeZone;
    }
    return "America/Sao_Paulo";
  }, [config]);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (timeZone && intervalData.dataFinal) {
      const now = dayjs();
      setIntervalData({
        dataInicial: now.subtract(1, "M").startOf("day"),
        dataFinal: now.startOf("day"),
        observacao: "",
        intervalosRemovidos: [],
      });
    }
  }, [timeZone]);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const res = await api.post("/easyReport/v2/findReport", { id: id });
          setRelatorioEditData(res.data);
          if (res.data.dataInicial && res.data.dataFinal) {
            setIntervalData((prev) => ({
              ...prev,
              dataInicial: dayjs(res.data.dataInicial),
              dataFinal: dayjs(res.data.dataFinal),
              observacao: res.data.observacao || "",
              intervalosRemovidos:
                res.data.intervalosRemovidos?.map((ir: any) => ({
                  start: ir.start ? dayjs(ir.start) : null,
                  end: ir.end ? dayjs(ir.end) : null,
                })) || [],
            }));
          }
        } catch (error) {
          console.error("Erro ao buscar relatório:", error);
        }
      })();
    }
  }, [id]);

  useEffect(() => {
    const currentPerfilIds = relatorioData.perfil.map((p) => p.id).sort();
    const prevIds = previousPerfilIds.sort();

    if (JSON.stringify(currentPerfilIds) !== JSON.stringify(prevIds)) {
      const newHostData = relatorioData.perfil.map((p) => {
        const existing = hostData.find((h) => h.perfilId === p.id);
        if (existing) return existing;
        return {
          perfilId: p.id,
          perfilTitle: p.titulo,
          variaveis: (p.variaveis ?? []).map((v) => ({
            id: v.id,
            titulo: v.title,
            item: v.keyName,
            hosts: [{ host: "", item: [] }],
            multiple: v.multiple,
          })),
        };
      });
      setHostData(newHostData);
      setPreviousPerfilIds(currentPerfilIds);
    }
  }, [relatorioData.perfil]);

  useEffect(() => {
    if (activeStep === 1 && showHostErrors) {
      setHostErrors(validateHosts(hostData));
    }
    if (activeStep === 0 && showRelatorioErrors) {
      setRelatorioErrors(validateRelatorio(relatorioData));
    }
  }, [
    hostData,
    activeStep,
    showHostErrors,
    showRelatorioErrors,
    relatorioData,
  ]);

  useEffect(() => {
    if (activeStep !== 0) {
      setShowRelatorioErrors(false);
      setRelatorioErrors({});
    }
    if (activeStep !== 1) {
      setShowHostErrors(false);
      setHostErrors([]);
    }
  }, [activeStep]);

  const previewData = useMemo(() => {
    if (perfil.length === 0) return { view: [], code: [], styles: [] };

    const processedPerfil = perfil.map((p) => {
      const newViews: string[] = [];
      const newStylesArr: Record<string, any>[] = [];
      for (let i = 0; i < p.styles.length; i++) {
        let newView = p.view[i];
        let newStyles: { [key: string]: any } = {};
        try {
          const stylesObj = JSON5.parse(p.styles[i]);
          Object.keys(stylesObj).forEach((styleKey) => {
            const newKey = `${styleKey}_${p.id}_${i}`;
            const regex = new RegExp(`\\b${styleKey}\\b`, "g");
            newView = newView.replace(regex, newKey);
            newStyles[newKey] = stylesObj[styleKey];
          });
        } catch (e) {
          newStyles = {};
        }
        newViews.push(newView);
        newStylesArr.push(newStyles);
      }
      return {
        ...p,
        view: newViews,
        styles: newStylesArr,
      };
    });

    return processedPerfil.reduce(
      (acc: { view: any[]; code: any[]; styles: any[] }, p) => {
        acc.view.push(...(p.view || []));
        acc.code.push(...(p.code || []));
        acc.styles.push(...(p.styles || []));
        return acc;
      },
      { view: [], code: [], styles: [] },
    );
  }, [perfil]);

  const variableNames = useMemo(
    () =>
      variaveisPreview.map((p) =>
        p?.map((v: any) => ({
          nome: v.variableName,
          itens: v.keyName.map((item: any) => item.key),
          multiple: v.multiple,
        })),
      ),
    [variaveisPreview, perfil],
  );

  const steps: StepType[] = [
    {
      label: "Configure o Relatório",
      description: (
        <ConfigureRelatorio
          relatorioData={relatorioData}
          setRelatorioData={setRelatorioData}
          setPerfil={setPerfil}
          setVariaveisPreview={setVariaveisPreview}
          relatorioEditData={relatorioEditData}
          isConfigInitialized={isConfigInitialized}
          setIsConfigInitialized={setIsConfigInitialized}
          errors={relatorioErrors}
          showErrors={showRelatorioErrors}
        />
      ),
    },
    {
      label: "Configure os Hosts",
      description: (
        <ConfigureHosts
          hostData={hostData}
          setHostData={setHostData}
          relatorioEditData={relatorioEditData}
          isEditDataProcessed={isEditDataProcessed}
          setIsEditDataProcessed={setIsEditDataProcessed}
          errors={hostErrors}
        />
      ),
    },
    {
      label: "Intervalo de Coleta",
      description: (
        <IntervaloColeta
          intervalData={intervalData}
          setIntervalData={setIntervalData}
        />
      ),
    },
  ];

  const handleNext = async () => {
    if (activeStep === 0) {
      setShowRelatorioErrors(true);
      const errors = validateRelatorio(relatorioData);
      setRelatorioErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }
    }
    if (activeStep === 1) {
      setShowHostErrors(true);
      const errors = validateHosts(hostData);
      setHostErrors(errors);
      if (errors.length > 0) {
        return;
      }
    }
    if (activeStep === steps.length - 1) {
      try {
        const logsString = localStorage.getItem("logs");
        const logsLoginClienteId = logsString ? JSON.parse(logsString)?.id : "";
        await api.post(
          "/easyReport/v2/createReport",
          {
            titulo: relatorioData.title,
            profiles: relatorioData.perfil.map((perfil, idx) => ({
              id: perfil.id,
              titulo: perfil.titulo,
              variaveis: (hostData[idx]?.variaveis ?? []).map((variavel) => ({
                id: variavel.id,
                titulo: variavel.titulo,
                item: variavel.item,
                hosts: variavel.hosts.map((h) => ({
                  hostId: h.host,
                  itemId: h.item,
                })),
                multiple: variavel.multiple,
              })),
              code: perfil.code,
              view: perfil.view,
              styles: perfil.styles,
              hasDefaultFunction: perfil.hasDefaultFunction,
            })),
            ocultarGraficos: relatorioData.hideGraphs,
            agendamento: true,
            dataInicial: intervalData.dataInicial.toISOString(),
            dataFinal: intervalData.dataFinal.toISOString(),
            observacao: intervalData.observacao,
            intervalosRemovidos: intervalData.intervalosRemovidos,
            logsLoginClienteId,
          },
          { responseType: "arraybuffer", timeout: 1_800_000 },
        );

        // const blob = new Blob([response.data], { type: "application/pdf" });
        // const url = window.URL.createObjectURL(blob);

        // const link = document.createElement("a");
        // link.href = url;
        // link.setAttribute(
        //   "download",
        //   `${relatorioData.title || "relatorio"}.pdf`,
        // );
        // document.body.appendChild(link);
        // link.click();

        // link.parentNode?.removeChild(link);
        // window.URL.revokeObjectURL(url);
        // notifyOnPage(
        //   window.location.pathname,
        //   "Sucesso ao gerar relatório.",
        //   "success",
        //   3000,
        // );
        navigate(`/easyReportV2/relatorios/`);
      } catch (error) {
        console.error("Erro ao criar relatório:", error);
        notifyOnPage(
          window.location.pathname,
          (error as any)?.response?.data?.error || "Erro ao gerar relatório.",
          "error",
          3000,
        );
      } finally {
        setRelatorioLoad(false);
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      // clear errors when leaving step
      setShowHostErrors(false);
      setHostErrors([]);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    // clear errors when leaving step
    setShowHostErrors(false);
    setHostErrors([]);
  };

  const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 10,
      left: "calc(-50% + 16px)",
      right: "calc(50% + 16px)",
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.success.main,
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.success.main,
      },
    },
  }));

  const verifyHosts = () => {
    const hasEmptyHosts = hostData.some((perfil) =>
      perfil.variaveis?.some(
        (variavel) =>
          !variavel.hosts.length ||
          variavel.hosts.some((h) => !h.host.trim() || !h.item.length),
      ),
    );
    return hasEmptyHosts;
  };

  const verifyStep = () => {
    if (activeStep === 0) {
      return !relatorioData.title.trim() || relatorioData.perfil.length === 0;
    }
    if (activeStep === 1) {
      return verifyHosts();
    }
    if (activeStep === 2) {
      const today = dayjs();
      const month = today.diff(intervalData.dataInicial, "month", true);

      const isSameDate = dayjs(intervalData.dataInicial).isSame(
        dayjs(intervalData.dataFinal),
      );
      return (
        !intervalData.dataFinal ||
        !intervalData.dataInicial ||
        month > monthLimit + 1 ||
        intervalData.dataInicial > intervalData.dataFinal ||
        isSameDate
      );
    }
  };

  const styles = {
    container: {
      width: "100%",
      maxWidth: "100%",
      height: "85vh",
      //paddingX: 2,
    },
    headerContainer: {
      display: "flex",
      alignItems: "center",
      justifyContents: "center",
      gap: 1,
      mb: 1,
    },
    paper: {
      padding: 3,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      width: "100%",
      height: { xs: "auto" },
      maxHeight: { xs: "none", md: "85vh" },
      overflowY: { xs: "visible", md: "auto" },
    },
    boxContent: {
      maxHeight: { xs: "none", md: "67vh" },
      overflowY: { xs: "visible", md: "auto" },
    },
    boxHeader: {
      display: "flex",
      height: "fit-content",
      justifyContent: "space-between",
      alignItems: "center",
    },
    button: {
      height: "40px",
    },
    boxReportViwer: {
      display: "flex",
      height: "85vh",
      gap: 2,
      width: { xs: "100%", md: "100%" },
      overflow: "hidden",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.background.paper,
      borderRadius: 1,
    },
    boxButtonStep: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      justifyContent: "flex-end",
      gap: 1,
    },
  };

  function validateHosts(hostData: HostDataType[]): HostErrorType[] {
    const errors: HostErrorType[] = [];
    hostData.forEach((perfil, perfilIdx) => {
      perfil.variaveis?.forEach((variavel, variavelIdx) => {
        // CUSTOM single input
        if (variavel.item?.[0]?.aplicacaoName === "CUSTOM") {
          const val = variavel.hosts?.[0]?.host ?? "";
          if (!val.trim()) {
            errors.push({
              perfilIdx,
              variavelIdx,
              hostIdx: 0,
              field: "custom",
              message: "Campo obrigatório",
            });
          }
          return;
        }

        // Regular host/item rows
        variavel.hosts?.forEach((host, hostIdx) => {
          if (!host.host.trim()) {
            errors.push({
              perfilIdx,
              variavelIdx,
              hostIdx,
              field: "host",
              message: "Campo obrigatório",
            });
          }
          if (!host.item.length) {
            errors.push({
              perfilIdx,
              variavelIdx,
              hostIdx,
              field: "item",
              message: "Campo obrigatório",
            });
          }
        });
      });
    });
    return errors;
  }

  function validateRelatorio(data: RelatorioDataType) {
    const errors: { title?: string; perfil?: string } = {};
    if (!data.title.trim()) errors.title = "Título obrigatório";
    if (!data.perfil.length) errors.perfil = "Selecione ao menos um perfil";
    return errors;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">Novo Relatório</Typography>
      </Box>
      <Grid2 container spacing={4} sx={styles.container}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={styles.paper}>
            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              connector={<QontoConnector />}
            >
              {steps.map(({ label }) => (
                <Step key={label}>
                  <StepLabel>{isMobile ? "" : label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={styles.boxContent}>{steps[activeStep].description}</Box>

            <Box sx={styles.boxButtonStep}>
              <Button
                color="secondary"
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                {"Voltar"}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  color: theme.palette.text.invert,
                  backgroundColor: theme.palette.secondary.main,
                }}
                disabled={verifyStep() || relatorioLoad}
                startIcon={
                  relatorioLoad && activeStep === steps.length - 1 ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CircularProgress size={20} color="inherit" />
                    </Box>
                  ) : undefined
                }
              >
                {activeStep === steps.length - 1
                  ? relatorioLoad
                    ? "Gerando..."
                    : "Gerar Relatório"
                  : "Continuar"}
              </Button>
            </Box>
          </Paper>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }} sx={{ height: "80vh" }}>
          <Box sx={styles.boxReportViwer}>
            <PdfPreview
              variableNames={variableNames}
              code={previewData.code}
              rawContent={previewData.view}
              styles={previewData.styles}
              verifyContent={true}
              watermark={true}
            />
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
}
